import type { IChatChannel } from '@/interfaces/chatChannel';

/**
 * Messaging channels the AI chat demo can be skinned as. The conversation
 * engine is the same for all of them (the LINE demo API); only the look, the
 * channel name and the default-by-region differ.
 */
export type { TChatChannel } from '@/interfaces/chatChannel';
import type { TChatChannel } from '@/interfaces/chatChannel';

export const CHAT_CHANNELS: Record<TChatChannel, IChatChannel> = {
  line: {
    id: 'line',
    name: 'LINE',
    slug: 'line-bot',
    icon: 'custom-brand-line',
    theme: 'green',
    sectionGradient: '!from-emerald-100/70 !to-teal-100/60',
    colors: {
      brand: '#06c755',
      onBrand: '#052e16',
      screen: '#91acc5',
      dateChip: '#4f6a82',
      userBubble: '#8de055',
      userText: '#172019',
      accent: '#087538',
      accentDark: '#64e59b',
      chip: '#ffffff',
      composer: '#f1f2f6',
    },
    copy: {
      en: {
        eyebrow: 'INTERACTIVE PREVIEW',
        title: 'Your customers ask on LINE. Obie answers and books.',
        account: 'Official account · Obie replies instantly',
        ariaPreview: 'LINE conversation preview',
      },
      zh: {
        eyebrow: '互動預覽',
        title: '顧客在 LINE 提問，Obie 立即回答並協助預約',
        account: '官方帳號 · Obie 即時回覆',
        ariaPreview: 'LINE 對話預覽',
      },
    },
  },
  whatsapp: {
    id: 'whatsapp',
    name: 'WhatsApp',
    slug: 'whatsapp-bot',
    icon: 'custom-brand-whatsapp',
    theme: 'teal',
    sectionGradient: '!from-teal-100/70 !to-emerald-100/60',
    colors: {
      brand: '#075e54',
      onBrand: '#ffffff',
      screen: '#e5ddd5',
      dateChip: '#d5ecf3',
      userBubble: '#dcf8c6',
      userText: '#111b21',
      accent: '#128c7e',
      accentDark: '#25d366',
      chip: '#ffffff',
      composer: '#ffffff',
    },
    copy: {
      en: {
        eyebrow: 'INTERACTIVE PREVIEW',
        title: 'Your customers message on WhatsApp. Obie answers and books.',
        account: 'Business account · Obie replies instantly',
        ariaPreview: 'WhatsApp conversation preview',
      },
      zh: {
        eyebrow: '互動預覽',
        title: '顧客在 WhatsApp 傳訊息，Obie 立即回答並協助預約',
        account: '商業帳號 · Obie 即時回覆',
        ariaPreview: 'WhatsApp 對話預覽',
      },
    },
  },
  wechat: {
    id: 'wechat',
    name: 'WeChat',
    slug: 'wechat-bot',
    icon: 'custom-brand-wechat',
    theme: 'green',
    sectionGradient: '!from-lime-100/60 !to-emerald-100/60',
    colors: {
      brand: '#ededed',
      onBrand: '#111111',
      screen: '#ededed',
      dateChip: '#dadada',
      userBubble: '#95ec69',
      userText: '#111111',
      accent: '#07c160',
      accentDark: '#5be49b',
      chip: '#ffffff',
      composer: '#f7f7f7',
    },
    copy: {
      en: {
        eyebrow: 'INTERACTIVE PREVIEW',
        title: 'Your customers message on WeChat. Obie answers and books.',
        account: 'Official account · Obie replies instantly',
        ariaPreview: 'WeChat conversation preview',
      },
      zh: {
        eyebrow: '互動預覽',
        title: '顧客在微信提問，Obie 立即回答並協助預約',
        account: '公眾號 · Obie 即時回覆',
        ariaPreview: '微信對話預覽',
      },
    },
  },
};

/** Shared primary-action colors for the LINE feature page. */
export const LINE_ACTION_COLORS = {
  background: CHAT_CHANNELS.line.colors.brand,
  text: '#ffffff',
};

/** Which channel to show a visitor first, from their CloudFront country code. */
export function chatChannelForCountry(
  countryCode: string | null
): TChatChannel {
  if (countryCode === 'HK' || countryCode === 'MO') return 'whatsapp';
  if (countryCode === 'CN') return 'wechat';
  return 'line';
}

export function isChatChannel(value: unknown): value is TChatChannel {
  return value === 'line' || value === 'whatsapp' || value === 'wechat';
}

export function chatChannelForFeatureSlug(slug: string): TChatChannel | null {
  const match = Object.values(CHAT_CHANNELS).find(
    channel => channel.slug === slug
  );
  return match?.id ?? null;
}
